import React from "react";
import moment from "moment";
import Checkbox from "../../../../../../../../components/shared/Checkbox/Checkbox";
import Spinner from "../../../../../../../../components/shared/spinner/Spinner";
import { Tooltip } from "../../../../../../../../components/shared/Tooltip/Tooltip";
import { VideoPathCell } from "../../../../../../components/Table/cells/VideoPathCell";
import { useMiniSwingList } from "../../../../hooks/useMiniSwingList";
import { useNewton } from "../../../../store/NewtonContext";

const getMissingFields = (video) => {
  const { metaData, userData } = video;
  const missing = [];

  // Check clubId (golfClub)
  if (!userData?.golfClub) {
    missing.push("Club");
  }

  // Check fps
  const fps = metaData?.fps;
  const fpsNum = typeof fps === "string" ? parseFloat(fps) : fps;
  if (fpsNum === undefined || fpsNum === null || Number.isNaN(fpsNum)) {
    missing.push("FPS");
  }

  // Check gender
  if (!userData?.gender) {
    missing.push("Gender");
  }

  // Check heightInInches (check both heightInInches and height)
  if (!userData?.height) {
    missing.push("Height");
  }

  // Check swingAngle (cameraAngle)
  if (!metaData?.cameraAngle) {
    missing.push("Camera Angle");
  }

  // Check swingIsLeftHanded (hand)
  if (!userData?.hand) {
    missing.push("Dominant Hand");
  }

  return missing;
};

const Table = () => {
  const { data, isLoading } = useMiniSwingList();
  const { selectedLibraryVideo, setSelectedLibraryVideo } = useNewton();

  const videos = data?.videos || [];

  const handleToggleRow = (video, value) => {
    if (value) {
      setSelectedLibraryVideo(video);
    } else {
      setSelectedLibraryVideo(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner color="primary500" size="md" />
      </div>
    );
  }

  if (!videos?.length) {
    return (
      <div className="text-gray600 flex h-64 items-center justify-center text-sm">
        No swings found.
      </div>
    );
  }

  return (
    <div className="overflow-auto">
      <table className="divide-gray200 min-w-full divide-y">
        <thead className="bg-gray50">
          <tr>
            <th className="text-gray800 px-2 py-3 text-left text-sm font-semibold">
              Video
            </th>
            <th className="text-gray800 px-2 py-3 text-left text-sm font-semibold">
              Date
            </th>
            <th className="text-gray800 px-2 py-3 text-left text-sm font-semibold">
              Club
            </th>
            <th className="text-gray800 px-2 py-3 text-left text-sm font-semibold">
              Tags
            </th>
            <th className="text-gray800 px-2 py-3 text-right text-sm font-semibold"></th>
          </tr>
        </thead>
        <tbody className="divide-gray100 divide-y">
          {videos.map((video) => {
            const isChecked = selectedLibraryVideo?.id === video.id;
            const fps = video?.metaData?.fps;
            // Parse fps to number if it's a string
            const fpsNum = typeof fps === "string" ? parseFloat(fps) : fps;
            const isLowFps =
              fpsNum !== undefined &&
              fpsNum !== null &&
              !isNaN(fpsNum) &&
              fpsNum < 60;
            const missingFields = getMissingFields(video);
            const hasMissingData = missingFields.length > 0;

            // Build tooltip content
            let tooltipContent = null;
            if (isLowFps || hasMissingData) {
              const messages = [];
              if (isLowFps) {
                messages.push(
                  `Video FPS (${fpsNum}) is below 60. Analysis requires at least 60 FPS.`
                );
              }
              if (hasMissingData) {
                messages.push(
                  `Missing required data: ${missingFields.join(", ")}`
                );
              }
              tooltipContent = messages.join("\n\n");
            }

            const isDisabled = isLowFps || hasMissingData;

            return (
              <tr className="hover:bg-gray50" key={video.id}>
                <td className="px-2 py-3">
                  <VideoPathCell actionType="none" row={{ original: video }} />
                </td>
                <td className="text-gray800 px-2 py-3 text-sm">
                  {video?.videoCreated
                    ? moment(Number(video.videoCreated)).format("MM/DD/YY")
                    : "--"}
                </td>
                <td className="text-gray800 px-2 py-3 text-sm">
                  {video?.userData?.golfClub || "—"}
                </td>
                <td className="px-2 py-3">
                  <div className="flex flex-wrap gap-2">
                    {(video?.tags || []).map((tag) => (
                      <span
                        className="bg-primary50 text-primary700 rounded-full px-2 py-0.5 text-xs font-semibold"
                        key={String(tag)}
                      >
                        {String(tag)}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-2 py-3">
                  <div className="flex justify-end">
                    <Tooltip
                      content={tooltipContent}
                      disabled={!tooltipContent}
                      placement="left"
                    >
                      <Checkbox
                        isChecked={isChecked}
                        isDisabled={isDisabled}
                        onChange={(val) => {
                          if (!isDisabled) {
                            handleToggleRow(video, val);
                          }
                        }}
                      />
                    </Tooltip>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default Table;
